import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  TrendingUp, TrendingDown, DollarSign, Package, Users, 
  BarChart3, Calendar, FileText, RefreshCw,
  ArrowUpRight, ArrowDownRight, Target, ShoppingBag, Percent, Award, Wallet, AlertCircle, Receipt,
  Eye, PieChart
} from 'lucide-react';
import { format, subMonths, startOfMonth } from 'date-fns';
import { fr } from 'date-fns/locale';
import useConfig from '../hooks/useConfig';
import { formatCurrency as formatCurrencyUtil, safeFixed, toNumber } from '../utils/formatters';

const COLORS = {
  reservations: '#3b82f6', // blue
  bar: '#f59e0b',          // amber
  restaurant: '#10b981',   // emerald
  spa: '#8b5cf6',          // violet
  boutique: '#ec4899',     // pink
  autres: '#6b7280'        // gray
};

const MONTHS_FR = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];

export default function ReportsPage() {
  const [dateRange, setDateRange] = useState({
    from: format(startOfMonth(subMonths(new Date(), 2)), 'yyyy-MM-dd'),
    to: format(new Date(), 'yyyy-MM-dd')
  });
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [activeTab, setActiveTab] = useState('annual');
  const [loading, setLoading] = useState(false);
  
  // Filtres avancés
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('quantity');
  const [categories, setCategories] = useState([]);
  
  // Report data
  const [annualData, setAnnualData] = useState(null);
  const [profitData, setProfitData] = useState(null);
  const [topProducts, setTopProducts] = useState(null);
  const [revenueChart, setRevenueChart] = useState(null);
  const [revenueByDates, setRevenueByDates] = useState(null);
  const [hrData, setHrData] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [addonData, setAddonData] = useState(null);
  const [globalKpis, setGlobalKpis] = useState(null);
  const [expensesChargesData, setExpensesChargesData] = useState(null);

  // Load channels from config
  const { options: configOptions, loading: configLoading } = useConfig(['CHANNEL']);
  const channels = configOptions.CHANNEL || [];

  // Fetch categories for filters
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await api.get('/categories');
        setCategories(res.data.filter(c => c.type !== 'EXPENSE'));
      } catch (e) {}
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    if (activeTab === 'annual') fetchAnnualReport();
    else if (activeTab === 'profit') fetchProfitReport();
    else if (activeTab === 'products') fetchTopProducts();
    else if (activeTab === 'revenue') { fetchRevenueChart(); fetchRevenueByDates(); }
    else if (activeTab === 'addons') fetchAddonReport();
    else if (activeTab === 'expenses-charges') fetchExpensesChargesReport();
    else if (activeTab === 'hr') fetchHRReport();
    else if (activeTab === 'audit') fetchAuditLogs();
    
    // Always fetch global KPIs
    fetchGlobalKpis();
  }, [activeTab, dateRange, selectedYear, categoryFilter, sortBy]);

  const fetchAnnualReport = async () => {
    setLoading(true);
    try {
      const res = await api.get('/reports/annual-financial', { params: { year: selectedYear } });
      setAnnualData(res.data);
    } catch (error) {
      toast.error('Erreur chargement rapport annuel');
    } finally {
      setLoading(false);
    }
  };

  const fetchProfitReport = async () => {
    setLoading(true);
    try {
      const res = await api.get('/reports/profit', { params: dateRange });
      setProfitData(res.data);
    } catch (error) {
      toast.error('Erreur chargement rapport bénéfices');
    } finally {
      setLoading(false);
    }
  };

  const fetchTopProducts = async () => {
    setLoading(true);
    try {
      const params = { ...dateRange, limit: 20, sort_by: sortBy };
      if (categoryFilter !== 'all') params.category_id = categoryFilter;
      const res = await api.get('/reports/top-products', { params });
      setTopProducts(res.data);
    } catch (error) {
      toast.error('Erreur chargement produits');
    } finally {
      setLoading(false);
    }
  };

  const fetchAddonReport = async () => {
    setLoading(true);
    try {
      const res = await api.get('/reports/addon-sales', { params: dateRange });
      setAddonData(res.data);
    } catch (error) {
      toast.error('Erreur chargement ventes additionnelles');
    } finally {
      setLoading(false);
    }
  };

  const fetchGlobalKpis = async () => {
    try {
      const res = await api.get('/reports/global-kpis', { params: dateRange });
      setGlobalKpis(res.data);
    } catch (error) {
      console.error('Global KPIs error:', error);
    }
  };

  const fetchExpensesChargesReport = async () => {
    setLoading(true);
    try {
      const res = await api.get('/reports/expenses-charges', { params: dateRange });
      setExpensesChargesData(res.data);
    } catch (error) {
      toast.error('Erreur chargement rapport dépenses & charges');
    } finally {
      setLoading(false);
    }
  };

  const fetchRevenueChart = async () => {
    setLoading(true);
    try {
      const res = await api.get('/reports/revenue-by-category', { params: { ...dateRange, groupBy: 'month' } });
      setRevenueChart(res.data);
    } catch (error) {
      toast.error('Erreur chargement graphique');
    } finally {
      setLoading(false);
    }
  };

  const fetchRevenueByDates = async () => {
    try {
      const res = await api.get('/reports/revenue-by-dates', { params: { year: selectedYear } });
      setRevenueByDates(res.data);
    } catch (error) {
      console.error('Revenue by dates error:', error);
    }
  };

  const fetchHRReport = async () => {
    setLoading(true);
    try {
      const res = await api.get('/reports/hr', { params: dateRange });
      setHrData(res.data);
    } catch (error) {
      toast.error('Erreur chargement rapport RH');
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    setLoading(true);
    try {
      const res = await api.get('/reports/audit', { params: { ...dateRange, limit: 100 } });
      setAuditLogs(res.data);
    } catch (error) {
      toast.error('Erreur chargement audit');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => formatCurrencyUtil(Math.round(toNumber(amount)), 'MAD');
  const formatPercent = (value) => safeFixed(value, 1) + '%';

  // Simple bar chart component
  const SimpleBarChart = ({ data, maxValue }) => {
    if (!data || data.length === 0) return null;
    const max = maxValue || Math.max(...data.map(d => d.total));
    
    return (
      <div className="space-y-2">
        {data.map((item, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <div className="w-16 text-xs text-muted-foreground">{item.label}</div>
            <div className="flex-1 h-8 bg-muted rounded-lg overflow-hidden flex">
              {Object.entries(COLORS).map(([key, color]) => {
                const value = item[key] || 0;
                const width = max > 0 ? (value / max) * 100 : 0;
                if (width === 0) return null;
                return (
                  <div 
                    key={key}
                    className="h-full transition-all"
                    style={{ width: `${width}%`, backgroundColor: color }}
                    title={`${key}: ${formatCurrency(value)}`}
                  />
                );
              })}
            </div>
            <div className="w-24 text-right text-sm font-medium">{formatCurrency(item.total)}</div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold">Rapports & Analyses</h1>
          <p className="text-muted-foreground mt-1">Tableaux de bord et statistiques</p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Date Range - only show for non-annual tabs */}
          {activeTab !== 'annual' && (
            <>
              <div className="flex items-center gap-2">
                <Label className="text-sm">Du</Label>
                <Input
                  type="date"
                  value={dateRange.from}
                  onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
                  className="w-36"
                />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm">Au</Label>
                <Input
                  type="date"
                  value={dateRange.to}
                  onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
                  className="w-36"
                />
              </div>
              <Button variant="outline" size="icon" onClick={() => {
                if (activeTab === 'profit') fetchProfitReport();
                else if (activeTab === 'products') fetchTopProducts();
                else if (activeTab === 'revenue') { fetchRevenueChart(); fetchRevenueByDates(); }
                else if (activeTab === 'expenses-charges') fetchExpensesChargesReport();
                else if (activeTab === 'hr') fetchHRReport();
                else if (activeTab === 'audit') fetchAuditLogs();
              }}>
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </>
          )}
          
          {/* Year selector for annual tab */}
          {activeTab === 'annual' && (
            <>
              <Label className="text-sm">Année</Label>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026, 2027].map(y => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" onClick={fetchAnnualReport}>
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-8 w-full max-w-5xl">
          <TabsTrigger value="annual" data-testid="tab-annual">
            <Target className="w-4 h-4 mr-1" />
            Suivi Annuel
          </TabsTrigger>
          <TabsTrigger value="profit" data-testid="tab-profit">
            <DollarSign className="w-4 h-4 mr-1" />
            Bénéfices
          </TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products">
            <Package className="w-4 h-4 mr-1" />
            Produits
          </TabsTrigger>
          <TabsTrigger value="addons" data-testid="tab-addons">
            <ShoppingBag className="w-4 h-4 mr-1" />
            Add-ons
          </TabsTrigger>
          <TabsTrigger value="expenses-charges" data-testid="tab-expenses-charges">
            <Receipt className="w-4 h-4 mr-1" />
            Sorties
          </TabsTrigger>
          <TabsTrigger value="revenue" data-testid="tab-revenue">
            <BarChart3 className="w-4 h-4 mr-1" />
            CA
          </TabsTrigger>
          <TabsTrigger value="hr" data-testid="tab-hr">
            <Users className="w-4 h-4 mr-1" />
            RH
          </TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">
            <FileText className="w-4 h-4 mr-1" />
            Audit
          </TabsTrigger>
        </TabsList>

        {/* ANNUAL TAB */}
        <TabsContent value="annual" className="space-y-6">
          {annualData && (
            <>
              {/* Top KPIs */}
              <div className="grid grid-cols-4 gap-4">
                {/* CA Année en cours */}
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-blue-600 font-medium">CA {annualData.year}</p>
                        <p className="text-2xl font-bold text-blue-800">{formatCurrency(annualData.totals.currentYear)}</p>
                        <div className="flex items-center mt-1">
                          {annualData.totals.yearOverYearChange >= 0 ? (
                            <ArrowUpRight className="h-4 w-4 text-green-600" />
                          ) : (
                            <ArrowDownRight className="h-4 w-4 text-red-600" />
                          )}
                          <span className={`text-xs font-medium ${annualData.totals.yearOverYearChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {annualData.totals.yearOverYearChange > 0 ? '+' : ''}{annualData.totals.yearOverYearChange}% vs {annualData.previousYear}
                          </span>
                        </div>
                      </div>
                      <TrendingUp className="h-8 w-8 text-blue-500" />
                    </div>
                  </CardContent>
                </Card>

                {/* Prévisions */}
                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-purple-600 font-medium">Prévisions</p>
                        <p className="text-2xl font-bold text-purple-800">{formatCurrency(annualData.forecast.total)}</p>
                        <p className="text-xs text-purple-600 mt-1">{annualData.forecast.reservationsCount} résa confirmées</p>
                      </div>
                      <Eye className="h-8 w-8 text-purple-500" />
                    </div>
                  </CardContent>
                </Card>

                {/* Projection annuelle extrapolée */}
                <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-green-600 font-medium">Projection annuelle</p>
                        <p className="text-2xl font-bold text-green-800">{formatCurrency(annualData.forecast.projectedAnnual)}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {annualData.forecast.projectedVsLastYear >= 0 ? (
                            <ArrowUpRight className="h-4 w-4 text-green-600" />
                          ) : (
                            <ArrowDownRight className="h-4 w-4 text-red-600" />
                          )}
                          <span className={`text-xs font-medium ${annualData.forecast.projectedVsLastYear >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {annualData.forecast.projectedVsLastYear > 0 ? '+' : ''}{annualData.forecast.projectedVsLastYear}% vs {annualData.previousYear}
                          </span>
                        </div>
                        <p className="text-[10px] text-green-600/70 mt-1">
                          Basé sur {annualData.forecast.monthsElapsed || annualData.currentMonth} mois écoulés
                        </p>
                      </div>
                      <Target className="h-8 w-8 text-green-500" />
                    </div>
                  </CardContent>
                </Card>

                {/* YTD */}
                <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-amber-600 font-medium">YTD (année en cours)</p>
                        <p className="text-2xl font-bold text-amber-800">{formatCurrency(annualData.ytd.current)}</p>
                        <div className="flex items-center mt-1">
                          {annualData.ytd.change >= 0 ? (
                            <ArrowUpRight className="h-4 w-4 text-green-600" />
                          ) : (
                            <ArrowDownRight className="h-4 w-4 text-red-600" />
                          )}
                          <span className={`text-xs font-medium ${annualData.ytd.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {annualData.ytd.change > 0 ? '+' : ''}{annualData.ytd.change}% vs {annualData.previousYear}
                          </span>
                        </div>
                      </div>
                      <Calendar className="h-8 w-8 text-amber-500" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* KPIs Section */}
              {annualData.kpis && (
                <div className="grid grid-cols-4 gap-4">
                  {/* Taux d'occupation */}
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-xs text-muted-foreground font-medium">Taux d'occupation</p>
                      <p className="text-2xl font-bold text-primary">{annualData.kpis.occupancyRate}%</p>
                      <p className="text-xs text-muted-foreground">{annualData.kpis.totalNights} nuits / 365</p>
                    </CardContent>
                  </Card>
                  
                  {/* % Annulation */}
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-xs text-muted-foreground font-medium">Taux d'annulation</p>
                      <p className={`text-2xl font-bold ${annualData.kpis.cancellationRate > 10 ? 'text-red-600' : 'text-green-600'}`}>
                        {annualData.kpis.cancellationRate}%
                      </p>
                      <p className="text-xs text-muted-foreground">{annualData.kpis.totalReservations} réservations</p>
                    </CardContent>
                  </Card>
                  
                  {/* Durée moyenne séjour */}
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-xs text-muted-foreground font-medium">Durée moy. séjour</p>
                      <p className="text-2xl font-bold">{annualData.kpis.avgStayDuration} <span className="text-base font-normal">nuits</span></p>
                      <p className="text-xs text-muted-foreground">par réservation</p>
                    </CardContent>
                  </Card>
                  
                  {/* Délai réservation */}
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-xs text-muted-foreground font-medium">Délai moy. réservation</p>
                      <p className="text-2xl font-bold">{annualData.kpis.avgBookingLeadTime} <span className="text-base font-normal">jours</span></p>
                      <p className="text-xs text-muted-foreground">avant arrivée</p>
                    </CardContent>
                  </Card>
                  
                  {/* Prix moyen nuit */}
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-xs text-muted-foreground font-medium">Prix moy. / nuit (ADR)</p>
                      <p className="text-2xl font-bold text-blue-600">{formatCurrency(annualData.kpis.avgDailyRate)}</p>
                      <p className="text-xs text-muted-foreground">CA / nuits vendues</p>
                    </CardContent>
                  </Card>
                  
                  {/* Revenu par client */}
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-xs text-muted-foreground font-medium">Revenu / client</p>
                      <p className="text-2xl font-bold text-green-600">{formatCurrency(annualData.kpis.revenuePerGuest)}</p>
                      <p className="text-xs text-muted-foreground">{annualData.kpis.totalGuests} clients</p>
                    </CardContent>
                  </Card>
                  
                  {/* % Direct */}
                  <Card className={annualData.kpis.directBookingRate >= 30 ? 'border-green-200 bg-green-50/30' : ''}>
                    <CardContent className="pt-4">
                      <p className="text-xs text-muted-foreground font-medium">Résa directes</p>
                      <p className={`text-2xl font-bold ${annualData.kpis.directBookingRate >= 30 ? 'text-green-600' : 'text-amber-600'}`}>
                        {annualData.kpis.directBookingRate}%
                      </p>
                      <p className="text-xs text-muted-foreground">sans commission</p>
                    </CardContent>
                  </Card>
                  
                  {/* Coût acquisition */}
                  <Card className={annualData.kpis.acquisitionCost > 15 ? 'border-red-200 bg-red-50/30' : ''}>
                    <CardContent className="pt-4">
                      <p className="text-xs text-muted-foreground font-medium">Coût d'acquisition</p>
                      <p className={`text-2xl font-bold ${annualData.kpis.acquisitionCost > 15 ? 'text-red-600' : 'text-green-600'}`}>
                        {annualData.kpis.acquisitionCost}%
                      </p>
                      <p className="text-xs text-muted-foreground">{formatCurrency(annualData.kpis.totalCommissions)} commissions</p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Mix Source - Dynamic */}
              {annualData.sourceMix && Object.keys(annualData.sourceMix).length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Répartition par source</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {Object.entries(annualData.sourceMix).map(([sourceCode, data]) => {
                        const channelConfig = channels.find(ch => ch.code === sourceCode);
                        const color = channelConfig?.color || channelConfig?.metadata?.color || '#6B7280';
                        const label = channelConfig?.label || sourceCode;
                        return (
                          <div key={sourceCode} className="text-center p-4 rounded-lg" style={{ backgroundColor: `${color}10` }}>
                            <div className="w-full h-3 bg-gray-200 rounded-full mb-2">
                              <div 
                                className="h-full rounded-full transition-all"
                                style={{ width: `${data.percent || 0}%`, backgroundColor: color }}
                              />
                            </div>
                            <p className="text-2xl font-bold" style={{ color }}>{data.percent || 0}%</p>
                            <p className="text-sm font-medium">{label}</p>
                            <p className="text-xs text-muted-foreground">{data.count || 0} résa</p>
                            <p className="text-xs text-muted-foreground">{formatCurrency(data.revenue || 0)}</p>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Comparaison mois actuel */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    Comparaison {annualData.currentMonthName} {annualData.year}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-6">
                    {/* Mois actuel */}
                    <div className="text-center p-4 bg-primary/5 rounded-lg">
                      <p className="text-sm text-muted-foreground mb-1">Ce mois</p>
                      <p className="text-3xl font-bold text-primary">{formatCurrency(annualData.currentMonthComparison.current.revenue)}</p>
                      <p className="text-xs text-muted-foreground mt-1">{annualData.currentMonthComparison.current.resaCount} réservations</p>
                    </div>
                    
                    {/* vs mois précédent */}
                    <div className="text-center p-4 bg-muted/50 rounded-lg">
                      <p className="text-sm text-muted-foreground mb-1">vs mois précédent</p>
                      <p className="text-2xl font-bold">{formatCurrency(annualData.currentMonthComparison.previousMonth.revenue)}</p>
                      <div className={`flex items-center justify-center mt-1 ${annualData.currentMonthComparison.previousMonth.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {annualData.currentMonthComparison.previousMonth.change >= 0 ? (
                          <ArrowUpRight className="h-4 w-4" />
                        ) : (
                          <ArrowDownRight className="h-4 w-4" />
                        )}
                        <span className="font-bold">
                          {annualData.currentMonthComparison.previousMonth.change > 0 ? '+' : ''}{annualData.currentMonthComparison.previousMonth.change}%
                        </span>
                      </div>
                    </div>
                    
                    {/* vs même mois année précédente */}
                    <div className="text-center p-4 bg-muted/50 rounded-lg">
                      <p className="text-sm text-muted-foreground mb-1">vs {annualData.currentMonthName} {annualData.previousYear}</p>
                      <p className="text-2xl font-bold">{formatCurrency(annualData.currentMonthComparison.sameMonthLastYear.revenue)}</p>
                      <div className={`flex items-center justify-center mt-1 ${annualData.currentMonthComparison.sameMonthLastYear.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {annualData.currentMonthComparison.sameMonthLastYear.change >= 0 ? (
                          <ArrowUpRight className="h-4 w-4" />
                        ) : (
                          <ArrowDownRight className="h-4 w-4" />
                        )}
                        <span className="font-bold">
                          {annualData.currentMonthComparison.sameMonthLastYear.change > 0 ? '+' : ''}{annualData.currentMonthComparison.sameMonthLastYear.change}%
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Graphique mensuel comparatif */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    Évolution mensuelle - {annualData.year} vs {annualData.previousYear}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {MONTHS_FR.map((month, idx) => {
                      const current = annualData.monthlyData.currentYear[idx];
                      const previous = annualData.monthlyData.previousYear[idx];
                      const maxRevenue = Math.max(
                        ...annualData.monthlyData.currentYear.map(m => m.revenue),
                        ...annualData.monthlyData.previousYear.map(m => m.revenue)
                      ) || 1;
                      const isFuture = idx > annualData.currentMonth - 1;
                      
                      return (
                        <div key={month} className="flex items-center gap-2">
                          <div className="w-10 text-xs text-muted-foreground font-medium">{month}</div>
                          <div className="flex-1 flex flex-col gap-1">
                            {/* Current year bar */}
                            <div className="flex items-center gap-2">
                              <div className="w-12 text-[10px] text-right text-muted-foreground">{annualData.year}</div>
                              <div className="flex-1 h-5 bg-muted/50 rounded overflow-hidden">
                                <div 
                                  className={`h-full transition-all ${isFuture ? 'bg-purple-400' : 'bg-blue-500'}`}
                                  style={{ width: `${(current.revenue / maxRevenue) * 100}%` }}
                                  title={`${formatCurrency(current.revenue)} - ${current.resaCount} résa`}
                                />
                              </div>
                              <div className="w-20 text-xs text-right font-medium">
                                {formatCurrency(current.revenue)}
                                {isFuture && <span className="text-purple-500 ml-1">*</span>}
                              </div>
                            </div>
                            {/* Previous year bar */}
                            <div className="flex items-center gap-2">
                              <div className="w-12 text-[10px] text-right text-muted-foreground">{annualData.previousYear}</div>
                              <div className="flex-1 h-4 bg-muted/30 rounded overflow-hidden">
                                <div 
                                  className="h-full bg-gray-400 transition-all"
                                  style={{ width: `${(previous.revenue / maxRevenue) * 100}%` }}
                                />
                              </div>
                              <div className="w-20 text-xs text-right text-muted-foreground">
                                {formatCurrency(previous.revenue)}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-center gap-6 mt-4 pt-4 border-t">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-blue-500" />
                      <span className="text-sm">{annualData.year} (réalisé)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-purple-400" />
                      <span className="text-sm">{annualData.year} (prévisionnel)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-gray-400" />
                      <span className="text-sm">{annualData.previousYear}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Insights */}
              {annualData.insights && (
                <div className="grid grid-cols-3 gap-4">
                  {annualData.insights.bestMonth && (
                    <Card className="border-green-200 bg-green-50/50">
                      <CardContent className="pt-4">
                        <p className="text-sm text-green-600 font-medium">Meilleur mois</p>
                        <p className="text-xl font-bold text-green-800">{MONTHS_FR[annualData.insights.bestMonth.month - 1]}</p>
                        <p className="text-sm text-green-700">{formatCurrency(annualData.insights.bestMonth.revenue)}</p>
                      </CardContent>
                    </Card>
                  )}
                  {annualData.insights.avgMonthlyRevenue > 0 && (
                    <Card className="border-blue-200 bg-blue-50/50">
                      <CardContent className="pt-4">
                        <p className="text-sm text-blue-600 font-medium">Moyenne mensuelle</p>
                        <p className="text-xl font-bold text-blue-800">{formatCurrency(annualData.insights.avgMonthlyRevenue)}</p>
                        <p className="text-sm text-blue-700">sur {annualData.insights.monthsWithRevenue} mois</p>
                      </CardContent>
                    </Card>
                  )}
                  <Card className="border-gray-200">
                    <CardContent className="pt-4">
                      <p className="text-sm text-muted-foreground font-medium">CA {annualData.previousYear}</p>
                      <p className="text-xl font-bold">{formatCurrency(annualData.totals.previousYear)}</p>
                      <p className="text-sm text-muted-foreground">année complète</p>
                    </CardContent>
                  </Card>
                </div>
              )}
            </>
          )}
          
          {!annualData && !loading && (
            <div className="text-center py-12 text-muted-foreground">
              Aucune donnée pour {selectedYear}
            </div>
          )}
        </TabsContent>

        {/* PROFIT TAB */}
        <TabsContent value="profit" className="space-y-6">
          {profitData && (
            <>
              {/* Avertissement outil de gestion */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">Outil de gestion indicatif</p>
                  <p className="text-amber-700">Les calculs de TVA et d'IS présentés sont des estimations approximatives à des fins de gestion. 
                  Ils ne remplacent pas une comptabilité officielle. Consultez votre expert-comptable pour vos déclarations fiscales.</p>
                </div>
              </div>

              {/* Calcul détaillé du bénéfice - Section principale */}
              <Card className="border-2 border-primary/20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-xl flex items-center gap-2">
                    <DollarSign className="h-6 w-6 text-primary" />
                    Calcul du Bénéfice
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Revenus */}
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="flex justify-between items-center text-lg mb-2">
                        <span className="font-semibold text-blue-700">Revenus Bruts</span>
                        <span className="font-bold text-blue-800">+{formatCurrency((profitData.revenue?.reservationsBrut || 0) + (profitData.revenue?.sales || 0))}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm text-blue-600 pl-4">
                        <span>Réservations</span>
                        <span className="text-right">{formatCurrency(profitData.revenue?.reservationsBrut || 0)}</span>
                        <span>Ventes</span>
                        <span className="text-right">{formatCurrency(profitData.revenue?.sales || 0)}</span>
                      </div>
                    </div>

                    {/* Commissions */}
                    <div className="flex justify-between items-center text-lg bg-amber-50 rounded-lg p-4">
                      <div>
                        <span className="font-semibold text-amber-700">Commissions plateformes</span>
                        <span className="text-sm text-amber-600 ml-2">({formatPercent(profitData.commissions?.avgRate || 0)} moy.)</span>
                      </div>
                      <span className="font-bold text-amber-800">-{formatCurrency(profitData.commissions?.total || profitData.revenue?.commissions || 0)}</span>
                    </div>

                    {/* Dépenses */}
                    <div className="flex justify-between items-center text-lg bg-red-50 rounded-lg p-4">
                      <span className="font-semibold text-red-700">Dépenses</span>
                      <span className="font-bold text-red-800">-{formatCurrency(profitData.sorties?.expenses || profitData.expenses?.total || 0)}</span>
                    </div>

                    {/* Charges */}
                    <div className="flex justify-between items-center text-lg bg-orange-50 rounded-lg p-4">
                      <span className="font-semibold text-orange-700">Charges</span>
                      <span className="font-bold text-orange-800">-{formatCurrency(profitData.sorties?.charges || profitData.charges?.total || 0)}</span>
                    </div>

                    {/* Bénéfice Brut */}
                    <div className={`flex justify-between items-center text-xl p-4 rounded-lg border-2 ${(profitData.profit?.brut || profitData.profit?.net || 0) >= 0 ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`}>
                      <span className="font-bold">= Bénéfice Brut</span>
                      <span className={`font-bold ${(profitData.profit?.brut || profitData.profit?.net || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {formatCurrency(profitData.profit?.brut || profitData.profit?.net || 0)}
                      </span>
                    </div>

                    {/* Taxes */}
                    <div className="bg-purple-50 rounded-lg p-4 space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-purple-700">TVA estimée ({profitData.taxes?.config?.tvaRate || 20}%)</span>
                        <span className="font-medium text-purple-800">-{formatCurrency(profitData.taxes?.tva?.amount || 0)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-purple-700">IS estimé ({profitData.taxes?.config?.isRate || 10}%)</span>
                        <span className="font-medium text-purple-800">-{formatCurrency(profitData.taxes?.is?.amount || 0)}</span>
                      </div>
                    </div>

                    {/* Bénéfice Net */}
                    <div className={`flex justify-between items-center text-2xl p-4 rounded-lg border-2 ${(profitData.taxes?.netAfterTaxes || 0) >= 0 ? 'bg-emerald-100 border-emerald-400' : 'bg-red-100 border-red-400'}`}>
                      <span className="font-bold">= Bénéfice Net Estimé</span>
                      <span className={`font-bold ${(profitData.taxes?.netAfterTaxes || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                        {formatCurrency(profitData.taxes?.netAfterTaxes || profitData.profit?.brut || profitData.profit?.net || 0)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* KPI Cards - 4 colonnes */}
              <div className="grid grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-blue-600 font-medium">Revenus Bruts</p>
                        <p className="text-xl font-bold text-blue-800">{formatCurrency((profitData.revenue?.reservationsBrut || 0) + (profitData.revenue?.sales || 0))}</p>
                      </div>
                      <TrendingUp className="h-6 w-6 text-blue-500" />
                    </div>
                    <p className="text-xs mt-1 text-muted-foreground">Résa + Ventes</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-amber-600 font-medium">Commissions</p>
                        <p className="text-xl font-bold text-amber-800">{formatCurrency(profitData.commissions?.total || profitData.revenue?.commissions || 0)}</p>
                      </div>
                      <Percent className="h-6 w-6 text-amber-500" />
                    </div>
                    <p className="text-xs mt-1 text-muted-foreground">{formatPercent(profitData.commissions?.avgRate || 0)} moy.</p>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-red-600 font-medium">Dépenses + Charges</p>
                        <p className="text-xl font-bold text-red-800">{formatCurrency((profitData.sorties?.total || 0) || ((profitData.expenses?.total || 0) + (profitData.charges?.total || 0)))}</p>
                      </div>
                      <TrendingDown className="h-6 w-6 text-red-500" />
                    </div>
                    <p className="text-xs mt-1 text-muted-foreground">Sorties totales</p>
                  </CardContent>
                </Card>
                
                <Card className={`bg-gradient-to-br ${(profitData.taxes?.netAfterTaxes || 0) >= 0 ? 'from-emerald-50 to-emerald-100 border-emerald-200' : 'from-red-50 to-red-100 border-red-200'}`}>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className={`text-xs font-medium ${(profitData.taxes?.netAfterTaxes || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>Bénéfice Net</p>
                        <p className={`text-xl font-bold ${(profitData.taxes?.netAfterTaxes || 0) >= 0 ? 'text-emerald-800' : 'text-red-800'}`}>
                          {formatCurrency(profitData.taxes?.netAfterTaxes || profitData.profit?.brut || profitData.profit?.net || 0)}
                        </p>
                      </div>
                      <Award className={`h-6 w-6 ${(profitData.taxes?.netAfterTaxes || 0) >= 0 ? 'text-emerald-500' : 'text-red-500'}`} />
                    </div>
                    <p className="text-xs mt-1 text-muted-foreground">Après taxes</p>
                  </CardContent>
                </Card>
              </div>

              {/* Revenus par Flux */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Wallet className="h-5 w-5" />
                    Revenus par Flux de Paiement
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {profitData.byFlux && Object.entries(profitData.byFlux).map(([fluxCode, flux]) => (
                      <div key={fluxCode} className="p-4 rounded-lg border" style={{ backgroundColor: `${flux.color}10`, borderColor: flux.color }}>
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: flux.color }} />
                          <span className="font-semibold">{flux.label}</span>
                          {flux.taxable ? (
                            <Badge variant="outline" className="text-xs bg-purple-100 text-purple-700">Taxé</Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs">Non taxé</Badge>
                          )}
                        </div>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span>Réservations</span>
                            <span className="font-medium">{formatCurrency(flux.reservations || 0)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Ventes</span>
                            <span className="font-medium">{formatCurrency(flux.sales || 0)}</span>
                          </div>
                          {(flux.manualIn || 0) > 0 && (
                            <div className="flex justify-between text-green-600">
                              <span>Entrées manuelles</span>
                              <span>+{formatCurrency(flux.manualIn)}</span>
                            </div>
                          )}
                          {(flux.expenses || 0) > 0 && (
                            <div className="flex justify-between text-red-600">
                              <span>Dépenses</span>
                              <span>-{formatCurrency(flux.expenses)}</span>
                            </div>
                          )}
                          {(flux.charges || 0) > 0 && (
                            <div className="flex justify-between text-orange-600">
                              <span>Charges</span>
                              <span>-{formatCurrency(flux.charges)}</span>
                            </div>
                          )}
                          <div className="flex justify-between border-t pt-1 font-bold">
                            <span>Net</span>
                            <span className={flux.net >= 0 ? 'text-green-700' : 'text-red-600'}>
                              {formatCurrency(flux.net || 0)}
                            </span>
                          </div>
                          {flux.taxable && flux.netAfterTaxes !== undefined && (
                            <div className="flex justify-between text-purple-600 text-xs">
                              <span>Après taxes</span>
                              <span>{formatCurrency(flux.netAfterTaxes)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Commissions par plateforme */}
              <div className="grid grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Commissions par plateforme</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {profitData.commissions?.byPlatform && Object.entries(profitData.commissions.byPlatform).map(([platform, data]) => (
                      <div key={platform} className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <Badge className={
                            platform === 'BOOKING' ? 'bg-blue-500' :
                            platform === 'AIRBNB' ? 'bg-pink-500' : 'bg-emerald-500'
                          }>
                            {platform}
                          </Badge>
                          <span className="text-sm text-muted-foreground">{data.count} résa</span>
                        </div>
                        <span className="font-medium">{formatCurrency(data.total)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between border-t pt-2">
                      <span className="font-medium">Taux moyen</span>
                      <span className="font-bold text-amber-600">{formatPercent(profitData.commissions?.avgRate || 0)}</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Dépenses par catégorie */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Dépenses par catégorie</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {(profitData.sorties?.byCategory || profitData.expenses?.byCategory) && Object.entries(profitData.sorties?.byCategory || profitData.expenses?.byCategory)
                        .sort(([,a], [,b]) => b - a)
                        .slice(0, 6)
                        .map(([category, amount]) => (
                          <div key={category} className="flex justify-between p-2 bg-muted/50 rounded">
                            <span className="text-sm">{category}</span>
                            <span className="font-medium">{formatCurrency(amount)}</span>
                          </div>
                        ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Note sur la configuration */}
              <div className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-lg">
                <span className="font-medium">Configuration des taxes:</span> Modifiez la taxabilité de chaque flux dans{' '}
                <a href="/settings?tab=config" className="text-primary underline">Paramètres → Configuration → Types de flux</a>.
              </div>
            </>
          )}
        </TabsContent>

        {/* EXPENSES & CHARGES TAB */}
        <TabsContent value="expenses-charges" className="space-y-6">
          {expensesChargesData && (
            <>
              {/* KPIs */}
              <div className="grid grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-red-600 font-medium">Total Dépenses</p>
                        <p className="text-2xl font-bold text-red-800">{formatCurrency(expensesChargesData.summary?.totalExpenses || 0)}</p>
                      </div>
                      <Receipt className="h-8 w-8 text-red-500" />
                    </div>
                    <p className="text-xs mt-1 text-muted-foreground">{expensesChargesData.summary?.expenseCount || 0} opérations</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-orange-600 font-medium">Total Charges</p>
                        <p className="text-2xl font-bold text-orange-800">{formatCurrency(expensesChargesData.summary?.totalCharges || 0)}</p>
                      </div>
                      <FileText className="h-8 w-8 text-orange-500" />
                    </div>
                    <p className="text-xs mt-1 text-muted-foreground">{expensesChargesData.summary?.chargeCount || 0} paiements</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-purple-600 font-medium">Total Sorties</p>
                        <p className="text-2xl font-bold text-purple-800">{formatCurrency(expensesChargesData.summary?.grandTotal || 0)}</p>
                      </div>
                      <TrendingDown className="h-8 w-8 text-purple-500" />
                    </div>
                    <p className="text-xs mt-1 text-muted-foreground">Dépenses + Charges</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-blue-600 font-medium">Catégories</p>
                        <p className="text-2xl font-bold text-blue-800">
                          {(expensesChargesData.expensesByCategory?.length || 0) + (expensesChargesData.chargesByCategory?.length || 0)}
                        </p>
                      </div>
                      <BarChart3 className="h-8 w-8 text-blue-500" />
                    </div>
                    <p className="text-xs mt-1 text-muted-foreground">actives</p>
                  </CardContent>
                </Card>
              </div>

              {/* Two columns: Expenses by category + Charges by category */}
              <div className="grid grid-cols-2 gap-6">
                {/* Dépenses par catégorie */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Receipt className="h-5 w-5 text-red-500" />
                      Dépenses par catégorie
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {expensesChargesData.expensesByCategory?.length > 0 ? (
                        expensesChargesData.expensesByCategory.map((cat) => {
                          const percent = expensesChargesData.summary.totalExpenses > 0 
                            ? Math.round((cat.total / expensesChargesData.summary.totalExpenses) * 100) 
                            : 0;
                          return (
                            <div key={cat.id || cat.name} className="flex items-center gap-3">
                              <div 
                                className="w-3 h-3 rounded-full shrink-0" 
                                style={{ backgroundColor: cat.color }}
                              />
                              <div className="flex-1">
                                <div className="flex justify-between mb-1">
                                  <span className="font-medium text-sm">{cat.name}</span>
                                  <span className="text-xs text-muted-foreground">{cat.count} op.</span>
                                </div>
                                <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                                  <div 
                                    className="h-full rounded-full transition-all"
                                    style={{ width: `${percent}%`, backgroundColor: cat.color }}
                                  />
                                </div>
                              </div>
                              <div className="text-right min-w-20">
                                <p className="font-bold text-sm">{formatCurrency(cat.total)}</p>
                                <p className="text-xs text-muted-foreground">{percent}%</p>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-4">Aucune dépense</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Charges par catégorie */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <FileText className="h-5 w-5 text-orange-500" />
                      Charges par catégorie
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {expensesChargesData.chargesByCategory?.length > 0 ? (
                        expensesChargesData.chargesByCategory.map((cat) => {
                          const percent = expensesChargesData.summary.totalCharges > 0 
                            ? Math.round((cat.total / expensesChargesData.summary.totalCharges) * 100) 
                            : 0;
                          return (
                            <div key={cat.id || cat.name} className="flex items-center gap-3">
                              <div 
                                className="w-3 h-3 rounded-full shrink-0" 
                                style={{ backgroundColor: cat.color }}
                              />
                              <div className="flex-1">
                                <div className="flex justify-between mb-1">
                                  <span className="font-medium text-sm">{cat.name}</span>
                                  <span className="text-xs text-muted-foreground">{cat.count} paie.</span>
                                </div>
                                <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                                  <div 
                                    className="h-full rounded-full transition-all"
                                    style={{ width: `${percent}%`, backgroundColor: cat.color }}
                                  />
                                </div>
                              </div>
                              <div className="text-right min-w-20">
                                <p className="font-bold text-sm">{formatCurrency(cat.total)}</p>
                                <p className="text-xs text-muted-foreground">{percent}%</p>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-4">Aucune charge</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Évolution mensuelle */}
              {expensesChargesData.monthlyEvolution?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Évolution mensuelle</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {expensesChargesData.monthlyEvolution.map((month) => {
                        const maxAmount = Math.max(...expensesChargesData.monthlyEvolution.map(m => m.total));
                        const expPercent = maxAmount > 0 ? (month.expenses / maxAmount) * 100 : 0;
                        const charPercent = maxAmount > 0 ? (month.charges / maxAmount) * 100 : 0;
                        return (
                          <div key={month.month} className="flex items-center gap-3">
                            <span className="w-20 text-sm text-muted-foreground">{month.month}</span>
                            <div className="flex-1 h-6 bg-muted/50 rounded overflow-hidden flex">
                              <div 
                                className="h-full bg-red-500 transition-all"
                                style={{ width: `${expPercent}%` }}
                                title={`Dépenses: ${formatCurrency(month.expenses)}`}
                              />
                              <div 
                                className="h-full bg-orange-500 transition-all"
                                style={{ width: `${charPercent}%` }}
                                title={`Charges: ${formatCurrency(month.charges)}`}
                              />
                            </div>
                            <div className="w-32 text-right">
                              <span className="font-medium">{formatCurrency(month.total)}</span>
                            </div>
                          </div>
                        );
                      })}
                      <div className="flex gap-4 justify-center mt-4 text-sm">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 bg-red-500 rounded" />
                          <span>Dépenses</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 bg-orange-500 rounded" />
                          <span>Charges</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Dernières transactions */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Dernières transactions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {expensesChargesData.recentTransactions?.map((tx) => (
                      <div key={`${tx.type}-${tx.id}`} className="flex items-center justify-between p-2 bg-muted/30 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-2 h-8 rounded-full" 
                            style={{ backgroundColor: tx.categoryColor }}
                          />
                          <div>
                            <p className="font-medium">{tx.name}</p>
                            <div className="text-xs text-muted-foreground flex items-center gap-2">
                              <span>{tx.category} • {tx.date}</span>
                              {tx.type === 'CHARGE' && (
                                <Badge variant="outline" className="text-xs">
                                  {tx.isOneTime ? 'Ponctuelle' : 'Récurrente'}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={tx.type === 'EXPENSE' ? 'destructive' : 'secondary'}>
                            {tx.type === 'EXPENSE' ? 'Dépense' : 'Charge'}
                          </Badge>
                          <span className="font-bold">{formatCurrency(tx.amount)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
          
          {!expensesChargesData && !loading && (
            <div className="text-center py-12 text-muted-foreground">
              Aucune donnée pour cette période
            </div>
          )}
        </TabsContent>

        {/* PRODUCTS TAB - ENHANCED WITH PROFITABILITY */}
        <TabsContent value="products" className="space-y-6">
          {/* Filters */}
          <Card>
            <CardContent className="py-3">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <Label className="text-sm">Catégorie</Label>
                  <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Toutes" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes</SelectItem>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id.toString()}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm">Trier par</Label>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="quantity">Quantité vendue</SelectItem>
                      <SelectItem value="revenue">Chiffre d'affaires</SelectItem>
                      <SelectItem value="margin">Marge générée</SelectItem>
                      <SelectItem value="margin_percent">% Marge</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {topProducts && (
            <>
              {/* KPIs Row */}
              <div className="grid grid-cols-5 gap-4">
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-blue-800">{topProducts.kpis?.totalQuantitySold || 0}</p>
                    <p className="text-xs text-blue-600">Articles vendus</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-green-800">{formatCurrency(topProducts.kpis?.totalRevenue || 0)}</p>
                    <p className="text-xs text-green-600">CA Total</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-purple-800">{formatCurrency(topProducts.kpis?.totalMargin || 0)}</p>
                    <p className="text-xs text-purple-600">Marge Totale</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-amber-800">{topProducts.kpis?.globalMarginPercent || 0}%</p>
                    <p className="text-xs text-amber-600">Marge Moyenne</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-cyan-50 to-cyan-100 border-cyan-200">
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-cyan-800">{formatCurrency(topProducts.kpis?.avgBasket || 0)}</p>
                    <p className="text-xs text-cyan-600">Panier Moyen</p>
                  </CardContent>
                </Card>
              </div>

              {/* Main Table with Profitability */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Package className="h-5 w-5" />
                    Analyse Rentabilité Produits
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left py-3 px-2">#</th>
                          <th className="text-left py-3 px-2">Produit</th>
                          <th className="text-right py-3 px-2">Prix Achat</th>
                          <th className="text-right py-3 px-2">Prix Vente</th>
                          <th className="text-right py-3 px-2">Marge Théo.</th>
                          <th className="text-right py-3 px-2">Qté</th>
                          <th className="text-right py-3 px-2">CA</th>
                          <th className="text-right py-3 px-2">Marge Réelle</th>
                          <th className="text-right py-3 px-2">% Marge</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topProducts.topProducts?.map((product, idx) => (
                          <tr key={product.id} className="border-b hover:bg-muted/50">
                            <td className="py-2 px-2 font-bold text-muted-foreground">{idx + 1}</td>
                            <td className="py-2 px-2">
                              <div className="font-medium">{product.name}</div>
                              <div className="text-xs text-muted-foreground">{product.category}</div>
                            </td>
                            <td className="py-2 px-2 text-right text-muted-foreground">{formatCurrency(product.buyPrice)}</td>
                            <td className="py-2 px-2 text-right">{formatCurrency(product.sellPrice)}</td>
                            <td className="py-2 px-2 text-right">
                              <span className={product.theoreticalMargin >= 0 ? 'text-green-600' : 'text-red-600'}>
                                {formatCurrency(product.theoreticalMargin)}
                              </span>
                            </td>
                            <td className="py-2 px-2 text-right font-medium">{product.quantitySold}</td>
                            <td className="py-2 px-2 text-right font-medium">{formatCurrency(product.totalRevenue)}</td>
                            <td className="py-2 px-2 text-right font-bold">
                              <span className={product.realMargin >= 0 ? 'text-green-600' : 'text-red-600'}>
                                {formatCurrency(product.realMargin)}
                              </span>
                            </td>
                            <td className="py-2 px-2 text-right">
                              <Badge variant={toNumber(product.realMarginPercent) >= 30 ? 'default' : toNumber(product.realMarginPercent) >= 15 ? 'secondary' : 'destructive'}>
                                {safeFixed(product.realMarginPercent, 1)}%
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Top/Low Performers & Categories */}
              <div className="grid grid-cols-3 gap-6">
                {/* Top by Margin */}
                <Card className="border-green-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2 text-green-700">
                      <Award className="h-4 w-4" />
                      Plus Rentables
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {topProducts.topByMargin?.slice(0, 5).map((p, i) => (
                      <div key={p.id} className="flex justify-between text-sm">
                        <span className="truncate flex-1">{i + 1}. {p.name}</span>
                        <span className="font-medium text-green-600">{formatCurrency(p.realMargin)}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Low Performers */}
                <Card className="border-red-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2 text-red-700">
                      <TrendingDown className="h-4 w-4" />
                      Moins Performants
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {topProducts.lowPerformers?.slice(0, 5).map((p, i) => (
                      <div key={p.id} className="flex justify-between text-sm">
                        <span className="truncate flex-1">{i + 1}. {p.name}</span>
                        <span className={`font-medium ${p.realMargin >= 0 ? 'text-amber-600' : 'text-red-600'}`}>
                          {formatCurrency(p.realMargin)}
                        </span>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* By Category with Margin */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <PieChart className="h-4 w-4" />
                      Par Catégorie
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {Object.entries(topProducts.byCategory || {})
                      .sort(([,a], [,b]) => b.margin - a.margin)
                      .slice(0, 5)
                      .map(([category, data]) => (
                        <div key={category}>
                          <div className="flex justify-between text-sm">
                            <span>{category}</span>
                            <span className="font-medium">{formatCurrency(data.revenue)}</span>
                          </div>
                          <div className="text-xs text-muted-foreground flex justify-between">
                            <span>{data.quantity} vendus</span>
                            <span className="text-green-600">Marge: {safeFixed(data.marginPercent, 1)}%</span>
                          </div>
                        </div>
                      ))}
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        {/* ADDON SALES TAB - NEW */}
        <TabsContent value="addons" className="space-y-6">
          {addonData && (
            <>
              {/* KPIs */}
              <div className="grid grid-cols-5 gap-4">
                <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-indigo-800">{addonData.kpis?.addonSalesCount || 0}</p>
                    <p className="text-xs text-indigo-600">Ventes Additionnelles</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-blue-800">{addonData.kpis?.addonRate || 0}%</p>
                    <p className="text-xs text-blue-600">Taux d'Add-on</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-green-800">{formatCurrency(addonData.kpis?.addonRevenue || 0)}</p>
                    <p className="text-xs text-green-600">CA Add-ons</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-purple-800">{addonData.kpis?.addonRevenueShare || 0}%</p>
                    <p className="text-xs text-purple-600">% du CA Total</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-amber-800">{formatCurrency(addonData.kpis?.avgAddonPerSale || 0)}</p>
                    <p className="text-xs text-amber-600">Moy. par Vente</p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-2 gap-6">
                {/* Top Addon Products */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <ShoppingBag className="h-5 w-5" />
                      Produits Add-on les Plus Vendus
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {addonData.topAddonProducts?.slice(0, 10).map((product, idx) => (
                        <div key={product.id} className="flex items-center gap-3">
                          <span className="w-6 font-bold text-muted-foreground">{idx + 1}</span>
                          <div className="flex-1">
                            <div className="flex justify-between">
                              <span className="font-medium">{product.name}</span>
                              <span className="text-green-600 font-medium">{formatCurrency(product.revenue)}</span>
                            </div>
                            <div className="flex justify-between text-xs text-muted-foreground">
                              <span>{product.category} • {product.quantitySold} vendus</span>
                              <span>Marge: {product.marginPercent}%</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* By Category */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <PieChart className="h-5 w-5" />
                      Add-ons par Catégorie
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {Object.entries(addonData.byCategory || {})
                      .sort(([,a], [,b]) => b.revenue - a.revenue)
                      .map(([category, data]) => {
                        const percent = addonData.kpis?.addonRevenue > 0 
                          ? (data.revenue / addonData.kpis.addonRevenue) * 100 
                          : 0;
                        return (
                          <div key={category}>
                            <div className="flex justify-between mb-1">
                              <span className="font-medium">{category}</span>
                              <span className="text-sm text-muted-foreground">{data.quantity} vendus</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-4 bg-muted rounded overflow-hidden">
                                <div 
                                  className="h-full bg-indigo-500 transition-all"
                                  style={{ width: `${percent}%` }}
                                />
                              </div>
                              <span className="w-24 text-right font-medium">{formatCurrency(data.revenue)}</span>
                            </div>
                            <div className="text-xs text-right text-green-600">
                              Marge: {formatCurrency(data.margin)}
                            </div>
                          </div>
                        );
                      })}
                  </CardContent>
                </Card>
              </div>

              {/* Insight Card */}
              <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
                <CardContent className="py-4">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-indigo-100 rounded-full">
                      <TrendingUp className="h-6 w-6 text-indigo-600" />
                    </div>
                    <div>
                      <p className="font-medium text-indigo-900">Performance des Ventes Additionnelles</p>
                      <p className="text-sm text-indigo-700">
                        {addonData.kpis?.addonRate >= 30 
                          ? `Excellent ! ${addonData.kpis.addonRate}% de vos ventes sont des add-ons liés aux réservations.`
                          : addonData.kpis?.addonRate >= 15
                          ? `Bon taux d'add-on à ${addonData.kpis.addonRate}%. Objectif: atteindre 30%.`
                          : `Taux d'add-on faible (${addonData.kpis?.addonRate || 0}%). Proposez plus de services aux clients en séjour.`
                        }
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {!addonData && !loading && (
            <div className="text-center py-12 text-muted-foreground">
              Sélectionnez une période et cliquez sur Rafraîchir
            </div>
          )}
        </TabsContent>

        {/* REVENUE CHART TAB */}
        <TabsContent value="revenue" className="space-y-6">
          {/* Nouveau: CA réalisé par dates avec taux de remplissage */}
          {revenueByDates && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  CA & Taux de remplissage {revenueByDates.year} vs {revenueByDates.previousYear}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left py-3 px-2">Mois</th>
                        <th className="text-center py-3 px-2">Nuits vendues</th>
                        <th className="text-center py-3 px-2">Taux rempl.</th>
                        <th className="text-center py-3 px-2">vs A-1</th>
                        <th className="text-right py-3 px-2">CA Résa</th>
                        <th className="text-right py-3 px-2">Commissions</th>
                        <th className="text-right py-3 px-2">CA Net</th>
                        <th className="text-right py-3 px-2">Ventes</th>
                        <th className="text-right py-3 px-2 font-bold">Total</th>
                        <th className="text-center py-3 px-2">vs A-1</th>
                      </tr>
                    </thead>
                    <tbody>
                      {revenueByDates.monthlyData.map((m) => (
                        <tr key={m.month} className="border-b hover:bg-muted/30">
                          <td className="py-2 px-2 font-medium capitalize">{m.monthName}</td>
                          <td className="py-2 px-2 text-center">{m.occupiedNights} / {m.daysInMonth}</td>
                          <td className="py-2 px-2 text-center">
                            <div className="flex items-center gap-2 justify-center">
                              <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                                <div 
                                  className={`h-full ${m.occupancyRate > 70 ? 'bg-green-500' : m.occupancyRate > 40 ? 'bg-amber-500' : 'bg-red-400'}`}
                                  style={{ width: `${m.occupancyRate}%` }}
                                />
                              </div>
                              <span className="font-medium">{m.occupancyRate}%</span>
                            </div>
                          </td>
                          <td className="py-2 px-2 text-center">
                            <span className={`text-xs font-medium ${m.occupancyChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {m.occupancyChange >= 0 ? '+' : ''}{m.occupancyChange}pts
                            </span>
                            <span className="text-xs text-muted-foreground ml-1">({m.prevOccupancyRate}%)</span>
                          </td>
                          <td className="py-2 px-2 text-right">{formatCurrency(m.caReservations)}</td>
                          <td className="py-2 px-2 text-right text-red-600">-{formatCurrency(m.commissions)}</td>
                          <td className="py-2 px-2 text-right">{formatCurrency(m.caNet)}</td>
                          <td className="py-2 px-2 text-right text-blue-600">{formatCurrency(m.caSales)}</td>
                          <td className="py-2 px-2 text-right font-bold text-primary">{formatCurrency(m.caTotal)}</td>
                          <td className="py-2 px-2 text-center">
                            <span className={`text-xs font-medium ${m.caChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {m.caChange >= 0 ? '+' : ''}{m.caChange}%
                            </span>
                          </td>
                        </tr>
                      ))}
                      {/* Totaux */}
                      <tr className="border-t-2 bg-primary/5 font-bold">
                        <td className="py-3 px-2">TOTAL {revenueByDates.year}</td>
                        <td className="py-3 px-2 text-center">{revenueByDates.totals.currentYear.occupiedNights}</td>
                        <td className="py-3 px-2 text-center">{revenueByDates.totals.currentYear.occupancyRate}%</td>
                        <td className="py-3 px-2 text-center">
                          <span className="text-xs text-muted-foreground">({revenueByDates.totals.previousYear.occupancyRate}%)</span>
                        </td>
                        <td className="py-3 px-2 text-right">{formatCurrency(revenueByDates.totals.currentYear.caReservations)}</td>
                        <td className="py-3 px-2 text-right text-red-600">-{formatCurrency(revenueByDates.totals.currentYear.commissions)}</td>
                        <td className="py-3 px-2 text-right">{formatCurrency(revenueByDates.totals.currentYear.caNet)}</td>
                        <td className="py-3 px-2 text-right text-blue-600">{formatCurrency(revenueByDates.totals.currentYear.caSales)}</td>
                        <td className="py-3 px-2 text-right text-primary">{formatCurrency(revenueByDates.totals.currentYear.caTotal)}</td>
                        <td className="py-3 px-2"></td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Comparaison annuelle */}
                <div className="grid grid-cols-3 gap-4 mt-6">
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="pt-4 text-center">
                      <p className="text-sm text-blue-600">CA Total {revenueByDates.year}</p>
                      <p className="text-2xl font-bold text-blue-800">{formatCurrency(revenueByDates.totals.currentYear.caTotal)}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gray-50 border-gray-200">
                    <CardContent className="pt-4 text-center">
                      <p className="text-sm text-gray-600">CA Total {revenueByDates.previousYear}</p>
                      <p className="text-2xl font-bold text-gray-800">{formatCurrency(revenueByDates.totals.previousYear.caTotal)}</p>
                    </CardContent>
                  </Card>
                  <Card className={`${revenueByDates.totals.currentYear.caTotal >= revenueByDates.totals.previousYear.caTotal ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                    <CardContent className="pt-4 text-center">
                      <p className="text-sm text-gray-600">Variation</p>
                      <p className={`text-2xl font-bold ${revenueByDates.totals.currentYear.caTotal >= revenueByDates.totals.previousYear.caTotal ? 'text-green-600' : 'text-red-600'}`}>
                        {revenueByDates.totals.previousYear.caTotal > 0 
                          ? `${Math.round(((revenueByDates.totals.currentYear.caTotal - revenueByDates.totals.previousYear.caTotal) / revenueByDates.totals.previousYear.caTotal) * 100)}%`
                          : '+∞'}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* HR TAB */}
        <TabsContent value="hr" className="space-y-6">
          {hrData && (
            <>
              {/* Summary */}
              <div className="grid grid-cols-5 gap-4">
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-primary">{hrData.summary.totalEmployees}</p>
                    <p className="text-xs text-muted-foreground">Employés</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-blue-600">{Math.round(hrData.summary.totalHoursWorked)}h</p>
                    <p className="text-xs text-muted-foreground">Heures travaillées</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-green-600">{hrData.summary.totalDaysWorked}</p>
                    <p className="text-xs text-muted-foreground">Jours travaillés</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-amber-600">{hrData.summary.totalDaysLeave}</p>
                    <p className="text-xs text-muted-foreground">Jours congé</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-red-600">{hrData.summary.totalDaysSick}</p>
                    <p className="text-xs text-muted-foreground">Jours maladie</p>
                  </CardContent>
                </Card>
              </div>

              {/* Employee details */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Détail par employé</CardTitle>
                </CardHeader>
                <CardContent>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2">Employé</th>
                        <th className="text-center py-2">Heures</th>
                        <th className="text-center py-2">Jours trav.</th>
                        <th className="text-center py-2">Congés</th>
                        <th className="text-center py-2">Maladie</th>
                        <th className="text-center py-2">Solde congés</th>
                        <th className="text-center py-2">Récup</th>
                        <th className="text-center py-2">Repos dus</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hrData.employees.map((emp) => (
                        <tr key={emp.id} className="border-b hover:bg-muted/50">
                          <td className="py-2">
                            <div className="font-medium">{emp.name}</div>
                            <div className="text-xs text-muted-foreground">{emp.position}</div>
                          </td>
                          <td className="py-2 text-center font-medium">{emp.totalHours}h</td>
                          <td className="py-2 text-center">{emp.daysWorked}</td>
                          <td className="py-2 text-center text-amber-600">{emp.daysLeave}</td>
                          <td className="py-2 text-center text-red-600">{emp.daysSick}</td>
                          <td className="py-2 text-center">
                            <Badge variant="outline" className="bg-green-50">{safeFixed(emp.balances?.leave, 1)}j</Badge>
                          </td>
                          <td className="py-2 text-center">
                            <Badge variant="outline" className="bg-amber-50">{safeFixed(emp.balances?.recovery, 1)}h</Badge>
                          </td>
                          <td className="py-2 text-center">
                            {(emp.balances?.restDaysOwed ?? 0) > 0 ? (
                              <Badge variant="outline" className="bg-blue-50">{emp.balances.restDaysOwed}</Badge>
                            ) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* AUDIT TAB */}
        <TabsContent value="audit" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Journal d'audit ({auditLogs.length} entrées)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="max-h-[600px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-background">
                    <tr className="border-b">
                      <th className="text-left py-2">Date/Heure</th>
                      <th className="text-left py-2">Utilisateur</th>
                      <th className="text-left py-2">Action</th>
                      <th className="text-left py-2">Entité</th>
                      <th className="text-left py-2">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.map((log) => (
                      <tr key={log.id} className="border-b hover:bg-muted/50">
                        <td className="py-2 text-xs">
                          {format(new Date(log.createdAt), 'dd/MM/yyyy HH:mm', { locale: fr })}
                        </td>
                        <td className="py-2">
                          <span className="font-medium">{log.user_name || log.user?.first_name || 'Système'}</span>
                        </td>
                        <td className="py-2">
                          <Badge variant="outline" className={
                            log.action.includes('CREATE') ? 'bg-green-50 text-green-700' :
                            log.action.includes('UPDATE') || log.action.includes('EDIT') ? 'bg-blue-50 text-blue-700' :
                            log.action.includes('DELETE') || log.action.includes('CANCEL') ? 'bg-red-50 text-red-700' :
                            'bg-gray-50'
                          }>
                            {log.action}
                          </Badge>
                        </td>
                        <td className="py-2 text-muted-foreground">
                          {log.entity_type} {log.entity_id ? `#${log.entity_id}` : ''}
                        </td>
                        <td className="py-2 text-xs max-w-xs truncate" title={log.description}>
                          {log.description || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
